/**
 * cursor.js
 * https://www.jqueryscript.net/animation/custom-cursor-magnetic.html
 * 
 * Przerobiona cursor.js (ESM version, no jQuery)


Klasa  | Opis efektu
-visible	| Kursor staje się widoczny. :before zaczyna się skalować (scale(0.2))
-active	| Kursor wciśnięty (mousedown). Delikatnie powiększa/zmniejsza :before
-pointer	| Po najechaniu na link, przycisk lub element z data-cursor. :before mniejsze (scale(0.15))
-text	| Kursor pokazuje tekst w .cb-cursor-text. :before powiększone (scale(1.7)), tekst widoczny
-opaque	| Kursor półprzezroczysty z trybem mix-blend-mode: soft-light
-exclusion	| Tryb mieszania mix-blend-mode: exclusion, tło #ececec
-normal	| Tryb mieszania normalny (mix-blend-mode: normal)
-inverse	| Kolor kursora biały
-lg	| Duże kółko (scale(2))
-xl	| Bardzo duże kółko (scale(3))
-hidden	| Kółko niewidoczne (scale(0))

// Ustawione w CSS.
-color-red	| color: red
-color-green	| color: #51c67d


Zastosowanie:
<!-- link → ma -pointer -->
<a href="#">Link</a>

<!-- element z tekstem w kursorze -->
<div data-cursor-text="Kliknij mnie!">Hover me</div>

<!-- duży kursor -->
<div data-cursor="-lg">Duży kursor</div>

<!-- przyciąganie do elementu -->
<div data-cursor-stick="#target">Hover me</div>
<div id="target">Cel przyciągania</div>

<!-- kolor czerwony -->
<div data-cursor="-color-red">Czerwony kursor</div>

<!-- kombinacja klas -->
<div data-cursor="-xl -color-green">Extra duży zielony</div>


 **/

import { gsap } from "gsap";

export class Cursor {
  constructor(options = {}) {
    this.options = Object.assign(
      {
        container: "body",
        speed: 0.7,
        ease: "expo.out",
        visibleTimeout: 300,
      },
      options
    );

    this.body = document.querySelector(this.options.container);
    if (!this.body) {
      console.warn("Cursor: container not found:", this.options.container);
      return;
    }

    this.el = document.createElement("div");
    this.el.className = "cb-cursor";
    this.text = document.createElement("div");
    this.text.className = "cb-cursor-text";
    this.el.appendChild(this.text);
    this.body.prepend(this.el);

    this.pos = { x: 0, y: 0 };
    this.visible = false;
    this.stick = null;

    this.bind();
    this.move(-window.innerWidth, -window.innerHeight, 0);
  }

  bind() {
    document.addEventListener("mouseleave", () => this.hide());
    document.addEventListener("mouseenter", () => this.show());
    document.addEventListener("mousemove", (e) => {
      this.pos = {
        x: this.stick
          ? this.stick.x - (this.stick.x - e.clientX) * 0.15
          : e.clientX,
        y: this.stick
          ? this.stick.y - (this.stick.y - e.clientY) * 0.15
          : e.clientY,
      };
      this.update();
    });

    document.addEventListener("mousedown", () => this.setState("-active"));
    document.addEventListener("mouseup", () => this.removeState("-active"));

    document.querySelectorAll("a, input, textarea, button").forEach((el) => {
      el.addEventListener("mouseenter", () => this.setState("-pointer"));
      el.addEventListener("mouseleave", () => this.removeState("-pointer"));
    });

    document.querySelectorAll("[data-cursor]").forEach((el) => {
      el.addEventListener("mouseenter", (e) =>
        this.setState(e.currentTarget.dataset.cursor)
      );
      el.addEventListener("mouseleave", (e) =>
        this.removeState(e.currentTarget.dataset.cursor)
      );
    });

    document.querySelectorAll("[data-cursor-text]").forEach((el) => {
      el.addEventListener("mouseenter", (e) =>
        this.setText(e.currentTarget.dataset.cursorText)
      );
      el.addEventListener("mouseleave", () => this.removeText());
    });

    document.querySelectorAll("[data-cursor-stick]").forEach((el) => {
      el.addEventListener("mouseenter", (e) =>
        this.setStick(e.currentTarget.dataset.cursorStick)
      );
      el.addEventListener("mouseleave", () => this.removeStick());
    });
  }

  /** =====================
   *   STANY / KLASY
   * ===================== */

  setState(state) {
    if (!state) return;
    this.el.classList.add(...state.trim().split(/\s+/));
  }

  removeState(state) {
    if (!state) return;
    this.el.classList.remove(...state.trim().split(/\s+/));
  }

  setText(text) {
    if (!text) return;
    this.text.innerHTML = text;
    this.el.classList.add("-text");
  }

  removeText() {
    this.el.classList.remove("-text");
  }

  /** =====================
   *   STICK (PRZYCIĄGANIE)
   * ===================== */

  setStick(selector) {
    const target = document.querySelector(selector);
    if (!target) return;
    const bound = target.getBoundingClientRect();
    this.stick = {
      y: bound.top + bound.height / 2,
      x: bound.left + bound.width / 2,
    };
    this.move(this.stick.x, this.stick.y, 0.5);
  }

  removeStick() {
    this.stick = null;
  }

  /** =====================
   *   ANIMACJE / RUCH
   * ===================== */

  update() {
    this.move();
    this.show();
  }

  move(x, y, duration) {
    if (!this.pos) return;
    gsap.to(this.el, {
      x: x ?? this.pos.x,
      y: y ?? this.pos.y,
      force3D: true,
      overwrite: true,
      ease: this.options.ease,
      duration: this.visible ? duration ?? this.options.speed : 0,
    });
  }

  show() {
    if (this.visible) return;
    clearTimeout(this.visibleInt);
    this.el.classList.add("-visible");
    this.visibleInt = setTimeout(() => (this.visible = true), 20);
  }

  hide() {
    clearTimeout(this.visibleInt);
    this.el.classList.remove("-visible");
    this.visibleInt = setTimeout(
      () => (this.visible = false),
      this.options.visibleTimeout
    );
  }
}

/** Inicjalizacja kursora */
export function initCursor(options) {
  
  // jeśli urządzenie dotykowe → przerwij
  if (window.innerWidth < 768) {
    return null;
  }

  return new Cursor(options);
}
